import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Typography,
  Box,
  useTheme,
  TablePagination,
  TableSortLabel,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Account } from '../../../types';
import { formatCurrency, getStatusColor } from '../../../utils/helpers';

type SortField = 'name' | 'balance' | 'creditLimit' | 'availableCredit' | 'paymentTerm';
type SortDirection = 'asc' | 'desc';

interface AccountListProps {
  accounts: Account[];
  onAccountClick: (account: Account) => void;
}

const AccountList: React.FC<AccountListProps> = ({ accounts, onAccountClick }) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Sort accounts
  const sortedAccounts = [...accounts].sort((a, b) => {
    let aValue: any;
    let bValue: any;
    
    switch (sortField) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'balance':
        aValue = a.balance;
        bValue = b.balance;
        break;
      case 'creditLimit':
        aValue = a.creditLimit;
        bValue = b.creditLimit;
        break;
      case 'availableCredit':
        aValue = a.availableCredit;
        bValue = b.availableCredit;
        break;
      case 'paymentTerm':
        aValue = a.paymentTerm;
        bValue = b.paymentTerm;
        break;
      default:
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
    }
    
    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Paginate accounts
  const paginatedAccounts = sortedAccounts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSort = (field: SortField) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (accounts.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No accounts found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Try adjusting your search or filters
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ mt: 2 }}>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="accounts table">
          <TableHead>
            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'name'}
                  direction={sortField === 'name' ? sortDirection : 'asc'}
                  onClick={() => handleSort('name')}
                  sx={{ fontWeight: 'bold' }}
                >
                  Account Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'balance'}
                  direction={sortField === 'balance' ? sortDirection : 'asc'}
                  onClick={() => handleSort('balance')}
                  sx={{ fontWeight: 'bold' }}
                >
                  Balance
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'creditLimit'}
                  direction={sortField === 'creditLimit' ? sortDirection : 'asc'}
                  onClick={() => handleSort('creditLimit')}
                  sx={{ fontWeight: 'bold' }}
                >
                  Credit Limit
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortField === 'availableCredit'}
                  direction={sortField === 'availableCredit' ? sortDirection : 'asc'}
                  onClick={() => handleSort('availableCredit')}
                  sx={{ fontWeight: 'bold' }}
                >
                  Available Credit
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  active={sortField === 'paymentTerm'}
                  direction={sortField === 'paymentTerm' ? sortDirection : 'asc'}
                  onClick={() => handleSort('paymentTerm')}
                  sx={{ fontWeight: 'bold' }}
                >
                  Payment Terms
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAccounts.map((account) => {
            const statusColor = getStatusColor(account.accountStatus);
            
            return (
              <TableRow
                key={account.contactID}
                sx={{
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    cursor: 'pointer',
                  },
                  '&:last-child td, &:last-child th': { border: 0 },
                }}
                onClick={() => onAccountClick(account)}
              >
                <TableCell component="th" scope="row">
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {account.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      HS ID: {account.hsID}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={account.accountStatus}
                    size="small"
                    sx={{
                      backgroundColor: `${statusColor}20`,
                      color: statusColor,
                      fontWeight: 'bold',
                      minWidth: 80,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: account.balance < 0 
                        ? theme.palette.success.main 
                        : account.balance > 0 
                        ? theme.palette.error.main 
                        : 'inherit'
                    }}
                  >
                    {formatCurrency(account.balance)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">
                    {account.creditLimit === 99999999 ? 'Unlimited' : formatCurrency(account.creditLimit)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    variant="body2"
                    sx={{
                      color: account.availableCredit < (account.creditLimit * 0.1) 
                        ? theme.palette.error.main 
                        : account.availableCredit < (account.creditLimit * 0.25)
                        ? theme.palette.warning.main
                        : theme.palette.success.main
                    }}
                  >
                    {formatCurrency(account.availableCredit)}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {account.paymentTerm} days
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAccountClick(account);
                    }}
                    sx={{
                      color: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: `${theme.palette.primary.main}10`,
                      },
                    }}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={sortedAccounts.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: 1,
          borderColor: 'divider',
        }}
      />
    </Paper>
  );
};

export default AccountList;
